// Pure decisions for the product updates banner, kept out of the container
// so they can be unit-tested without a browser.
//
// After Slowed updates to a new release line (0.11 -> 0.12), the panel says so
// once. What it says belongs to the release: 0.12 announces Slowed on
// SoundCloud and Slowed Studio at studio.slowed.app, and opens Studio (a
// SoundCloud link would only lead to Chrome's permission ask on arrival);
// a release with nothing of its own to say
// offers the changelog. Fix releases (0.12.0 -> 0.12.1) stay quiet: a note on
// every patch would train people to ignore it.
//
// The record lives in chrome.storage.local as `productUpdate`, because which
// version is installed is a fact about this browser, not the account. It is
// additive: versions that predate it never read or write the key.

// Where the tool lives when the banner is only being previewed and the site
// has not started serving `toolUrl` yet.
export const PREVIEW_TOOL_URL = "https://studio.slowed.app/";

const CHANGELOG_CONTENT = {
  kind: "changelog",
  text: "Slowed just updated",
  cta: "What’s new",
  url: null,
};

// One entry per release line that has something of its own to announce.
const RELEASE_CONTENT = {
  "0.12": ({ toolUrl, version }) =>
    // Held, not skipped, until the site serves the tool's address: the
    // extension can reach people before studio.slowed.app is up, and a banner
    // that opens a dead page is worse than one that arrives a day late.
    toolUrl
      ? {
          kind: "tool",
          text: "New: SoundCloud + Slowed Studio",
          cta: "Try Studio",
          url: buildToolUrl(toolUrl, { medium: "update-banner", version }),
        }
      : null,
};

// Tags where the visit came from, so the tool can tell the extension's
// banner from its off-YouTube link. Nothing about the listener is attached.
export function buildToolUrl(toolUrl, { medium, version } = {}) {
  if (!toolUrl) return null;
  try {
    const url = new URL(toolUrl);
    url.searchParams.set("utm_source", "slowed-extension");
    if (medium) url.searchParams.set("utm_medium", medium);
    if (version) url.searchParams.set("utm_campaign", String(version));
    return url.toString();
  } catch (_) {
    return null;
  }
}

// What the banner says for `version`, or null while its content is held.
export function resolveUpdateBannerContent({ version, toolUrl } = {}) {
  const line = releaseLine(version);
  if (!line) return null;
  const content = RELEASE_CONTENT[line.join(".")];
  return content ? content({ toolUrl, version }) : CHANGELOG_CONTENT;
}

function releaseLine(version) {
  const match = /^(\d+)\.(\d+)(?:\.\d+)?/.exec(String(version ?? "").trim());
  return match ? [Number(match[1]), Number(match[2])] : null;
}

// True when `version` belongs to a later release line than `seenVersion`.
// An unreadable current version never announces anything; an unreadable or
// missing seen version counts as "older than anything".
export function isNewerReleaseLine(version, seenVersion) {
  const current = releaseLine(version);
  if (!current) return false;
  const seen = releaseLine(seenVersion);
  if (!seen) return true;
  if (current[0] !== seen[0]) return current[0] > seen[0];
  return current[1] > seen[1];
}

// A missing record means an install that predates the banner, so it is an
// update worth announcing: fresh installs are stamped by the background the
// moment they happen (see stampInstalledVersion) and never reach here empty.
//
// Closing the panel without answering leaves it up for next time, but only
// `maxShows` times per release, so it can never become furniture.
export function computeUpdateBannerVisible({
  version,
  productUpdate,
  content,
  notSupported,
  musicProOnly,
  maxShows = 3,
} = {}) {
  if (notSupported === true || musicProOnly === true) return false;
  if (!content) return false;
  if (!isNewerReleaseLine(version, productUpdate?.seenVersion)) return false;
  if (
    productUpdate?.shownVersion === version &&
    (Number(productUpdate?.shownCount) || 0) >= (Number(maxShows) || 0)
  ) {
    return false;
  }
  return true;
}

// Persisted the moment the banner is rendered. The count belongs to one
// version, so the next release starts from zero.
export function markUpdateBannerShown(productUpdate, { version } = {}) {
  const sameVersion = productUpdate?.shownVersion === version;
  return {
    ...(productUpdate || {}),
    shownVersion: version,
    shownCount: (sameVersion ? Number(productUpdate?.shownCount) || 0 : 0) + 1,
  };
}

// "Got it", "What's new", and a fresh install all end the same way: this
// version has been seen, and nothing is announced until the next release line.
export function markUpdateSeen(
  productUpdate,
  { version, now = Date.now() } = {},
) {
  return {
    ...(productUpdate || {}),
    seenVersion: version,
    seenAt: new Date(now).toISOString(),
  };
}

// Someone who installed this version has nothing to be told about it.
export function stampInstalledVersion(productUpdate, options) {
  return markUpdateSeen(productUpdate, options);
}
